import {
  Injectable,
  Signal,
  WritableSignal,
  inject,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { RestApiService } from '@core/services/rest-api.service';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { firstValueFrom } from 'rxjs';
import {
  SignalFormElementConfig,
  VtSignalFormElement,
} from '../interfaces/signal-form-element.config';
import { TranslateService } from '@ngx-translate/core';
import {
  DvDropdownElementParams,
  DvDropdownParams,
  DvRadioGroupParams,
} from '@devinco/ui/dds';
import { DropdownContext } from '@shared/dv-form/interfaces/dropdown-context';
import { FieldTree } from '@angular/forms/signals';
import { GetDirtyValuesOptions } from '../interfaces/signal-form-common';
import {
  DvFormElementGroupParams,
  DvSignalDropdownParams,
} from '@devinco/ui/signal-forms';

type RawRow = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class SignalFormService {
  private api = inject(RestApiService);
  private translate = inject(TranslateService);

  /**
   * Returns only the fields whose FieldTree node reports dirty() === true,
   * keyed by their form path — walking element-group/nested configs the same
   * way SignalFormService.traverse does.
   */
  getDirtyValues<T extends object>(
    fieldTree: FieldTree<T>,
    elements: VtSignalFormElement<T>[],
    options: GetDirtyValuesOptions = {}
  ): Partial<T> {
    const { reduceDropdownsToId = 'all' } = options;
    const result: Partial<T> = {};

    const visit = (els: VtSignalFormElement<T>[]): void => {
      for (const element of els) {
        const el = typeof element === 'function' ? element() : element;

        if (!el.path || el.extra?.ignoreInRequest) {
          continue;
        }

        const node = (fieldTree as any)[el.path as string];
        const state = node?.();

        if (!state?.dirty()) {
          continue;
        }

        let value = state.value();

        if (el.type === 'dropdown') {
          const props = el.props as DvDropdownElementParams;
          const idField = props.idField;
          const isMultiSelect = props.isMultiSelect === true;

          const shouldReduce =
            reduceDropdownsToId === 'all' ||
            (reduceDropdownsToId === 'single' && !isMultiSelect) ||
            (reduceDropdownsToId === 'multi' && isMultiSelect);

          if (idField) {
            if (shouldReduce) {
              value = this.reduceDropdownValue(value, idField);
            } else if (reduceDropdownsToId !== 'none') {
              value = this.mapDropdownValueToObject(value, idField);
            }
          }
        }

        (result as Record<string, unknown>)[el.path as string] = value;
      }
    };

    visit(elements);

    return result;
  }

  /**
   * Builds reactive resources for a signal form:
   * fetches the entity by id, merges it onto `defaultValue`, and exposes
   * `formModel`, `isLoading` and dropdown `selectedItems` signals.
   */
  getFormResources<T extends object>(config: {
    defaultValue: T;
    entityId: Signal<number | string | null | undefined>;
    entity: Entity;
    elements: VtSignalFormElement<T>[];
  }): {
    formModel: WritableSignal<T>;
    selectedItems: Record<string, WritableSignal<unknown[]>>;
  } {
    const { defaultValue, entityId, entity, elements: elementsConfig } = config;
    const elementsAny =
      elementsConfig as unknown as SignalFormElementConfig<unknown>[];
    const selectedItems = this.ensureSelectedItemsSignals(elementsAny);
    const seededDefault = this.seedDefaultsFromElements(
      defaultValue,
      elementsAny
    );

    const formKeys: string[] = [];
    this.traverse(elementsAny, el => {
      if (el.path && !el.extra?.ignoreInRequest) {
        formKeys.push(el.path as string);
      }
    });
    const displayKeys = this.getDisplayKeys(elementsAny);
    const allKeys = Array.from(new Set([...formKeys, ...displayKeys]));

    const combinedResource = resource({
      params: () => ({ entityId: entityId() }),
      loader: async ({ params }) => {
        const id = params.entityId;
        for (const items of Object.values(selectedItems)) {
          items.set([]);
        }
        if (
          id === null ||
          id === undefined ||
          id === '' ||
          id === NEW_ENTITY_ROUTE_PARAM
        ) {
          return null;
        }
        let rawData: RawRow | null;
        try {
          rawData = await firstValueFrom(
            this.api.getInfo<RawRow>(entity, String(id), allKeys)
          );
        } catch {
          return null;
        }
        if (!rawData) return null;
        this.applyDisplayData(elementsAny, rawData, selectedItems);
        const reducedData = this.reduceDropdownFieldsForFormModel(
          elementsAny,
          rawData
        );
        return { formValue: this.mergeSchema(seededDefault, reducedData) as T };
      },
    });

    const formModel = linkedSignal(
      () => combinedResource.value()?.formValue ?? seededDefault
    );

    return {
      formModel,
      selectedItems,
    };
  }

  /**
   * Converts dropdown fields in raw API data to primitive id(s) via `idField`
   * before merging into the form model.
   */
  private reduceDropdownFieldsForFormModel(
    elements: SignalFormElementConfig<unknown>[],
    data: RawRow
  ): RawRow {
    const reduced: RawRow = { ...data };
    this.traverse(elements, el => {
      if (!el.path || el.type !== 'dropdown') return;
      const path = el.path as string;
      if (path.includes('.')) return;
      const idField = (el.props as DvSignalDropdownParams).idField;
      const raw = reduced[path];
      if (!idField || raw === undefined || raw === null) return;
      reduced[path] = this.reduceDropdownValue(raw, idField);
    });
    return reduced;
  }

  /**
   * Converts dropdown IDs into entity reference objects.
   *
   * Example:
   * 5 -> { CountryId: 5 }
   * [5, 8] -> [{ CountryId: 5 }, { CountryId: 8 }]
   */
  private mapDropdownValueToObject(value: unknown, idField: string): unknown {
    if (Array.isArray(value)) {
      return value.map(item => ({
        [idField]: item,
      }));
    }

    return {
      [idField]: value,
    };
  }

  /**
   * Converts dropdown objects into IDs.
   *
   * Example:
   * { CountryId: 5 } -> 5
   * [{ CountryId: 5 }, { CountryId: 8 }] -> [5, 8]
   */
  private reduceDropdownValue(value: unknown, idField: string): unknown {
    if (Array.isArray(value)) {
      return value.map(row => this.reduceRow(row, idField));
    }

    return this.reduceRow(value, idField);
  }

  /**
   * Extracts an ID from a dropdown object.
   */
  private reduceRow(row: unknown, idField: string): unknown {
    if (row && typeof row === 'object') {
      return (row as Record<string, unknown>)[idField];
    }

    return row;
  }

  private getDisplayKeys(
    elements: SignalFormElementConfig<unknown>[]
  ): string[] {
    const keys: string[] = [];
    this.traverse(elements, el => {
      if (el.extra?.extraDisplayDataFields?.length) {
        keys.push(...el.extra.extraDisplayDataFields);
      }
      if (el.extra?.flatDisplayField?.source) {
        keys.push(el.extra.flatDisplayField.source);
      }
    });
    return keys;
  }

  /**
   * Seeds the default model with every path declared in the elements so the
   * resulting object has a key for each form field. Angular's signal `form()`
   * derives its FieldTree children from `Object.keys(value)`, so missing keys
   * yield `undefined` children and downstream `[formField]` bindings crash.
   * User-supplied defaults take precedence; unspecified paths default to a
   * type-appropriate empty value based on the element's control type.
   */
  private seedDefaultsFromElements<T extends object>(
    defaultValue: T,
    elements: SignalFormElementConfig<unknown>[]
  ): T {
    const seeded = { ...(defaultValue as Record<string, unknown>) };
    this.traverse(elements, el => {
      if (!el.path) return;
      const path = el.path as string;
      if (path.includes('.')) return;
      if (!(path in seeded)) {
        seeded[path] = this.getDefaultValueForElement(el);
      }
    });
    return seeded as T;
  }

  /**
   * Ensures every dropdown element in the tree has a `props.selectedItems`
   * signal (creating an empty one if the element config didn't set one),
   * so the dropdown always has somewhere to receive its display rows.
   * Returns the signals keyed by path.
   */
  private ensureSelectedItemsSignals(
    elements: SignalFormElementConfig<unknown>[]
  ): Record<string, WritableSignal<unknown[]>> {
    const selectedItems: Record<string, WritableSignal<unknown[]>> = {};
    this.traverse(elements, el => {
      if (!el.path || el.type !== 'dropdown') return;
      const props = el.props as DvSignalDropdownParams;
      props.selectedItems ??= signal<unknown[]>([]);
      selectedItems[el.path as string] = props.selectedItems;
    });
    return selectedItems;
  }

  /**
   * Computes each dropdown's display rows from the raw entity data and
   * pushes them into that dropdown's `selectedItems` signal.
   */
  private applyDisplayData<T = any>(
    elements: SignalFormElementConfig<unknown>[],
    data: RawRow,
    selectedItems: Record<string, WritableSignal<unknown[]>>
  ): void {
    this.traverse(elements, el => {
      if (!el.path || el.type !== 'dropdown') return;
      const path = el.path as string;
      const raw = this.getNestedValue(data, path);
      if (raw === undefined || raw === null) return;

      const props = el.props as DvSignalDropdownParams<T>;
      const contextTranslateLabels = (
        props.context as DropdownContext<T> | undefined
      )?.translateLabels;
      const translateFields =
        el.extra?.translateLabels ?? contextTranslateLabels;

      let rows: RawRow[];
      const flatMap = el.extra?.flatDisplayField;
      if (flatMap && !Array.isArray(raw) && typeof raw !== 'object') {
        // scalar id + sibling flat field on the same entity — synthesize a row
        const idField = props.idField;
        const labelValue = this.getNestedValue(data, flatMap.source);
        rows = [
          {
            ...(idField ? { [idField]: raw } : {}),
            [flatMap.target]: labelValue,
          },
        ];
      } else {
        rows = Array.isArray(raw) ? raw : [raw];
      }

      const translatedRows = translateFields?.length
        ? rows.map(row => this.translateRow(row, translateFields))
        : rows;
      selectedItems[path].set(translatedRows);
    });
  }

  private translateRow(row: RawRow, fields: string[]): RawRow {
    if (!row || typeof row !== 'object') return row;
    const translated: RawRow = { ...row };
    for (const field of fields) {
      const value = translated[field];
      if (typeof value === 'string') {
        translated[field] = this.translate.instant(value);
      }
    }
    return translated;
  }

  private traverse(
    elements: SignalFormElementConfig<unknown>[],
    visit: (el: SignalFormElementConfig<unknown>) => void
  ): void {
    for (const el of elements) {
      visit(el);
      if (el.type === 'element-group') {
        const nested = (el.props as DvFormElementGroupParams)
          .elements as SignalFormElementConfig<unknown>[];
        this.traverse(nested, visit);
      }
    }
  }

  private getNestedValue(obj: RawRow, path: string): unknown {
    return path
      .split('.')
      .reduce<unknown>(
        (acc, key) =>
          acc && typeof acc === 'object' ? (acc as RawRow)[key] : undefined,
        obj
      );
  }

  private mergeSchema(defaultVal: unknown, rawVal: unknown): unknown {
    const isPlainObject = (v: unknown): v is Record<string, unknown> =>
      !!v && typeof v === 'object' && !Array.isArray(v);
    if (isPlainObject(defaultVal)) {
      if (rawVal === undefined || rawVal === null) {
        const out: Record<string, unknown> = {};
        for (const key of Object.keys(defaultVal)) {
          out[key] = this.mergeSchema(defaultVal[key], undefined);
        }
        return out;
      }
      if (isPlainObject(rawVal)) {
        const out: Record<string, unknown> = {};
        for (const key of Object.keys(defaultVal)) {
          out[key] = this.mergeSchema(defaultVal[key], rawVal[key]);
        }
        return out;
      }
    }
    if (rawVal === undefined || rawVal === null)
      return structuredClone(defaultVal);
    return rawVal;
  }

  private getDefaultValueForElement(
    el: SignalFormElementConfig<unknown>
  ): unknown {
    switch (el.type) {
      case 'text':
      case 'textarea':
      case 'search':
      case 'password':
      case 'date':
      case 'datetime':
      case 'time':
      case 'dropdown-phone':
      case 'dropdown-country':
        return '';
      case 'checkbox':
      case 'toggle':
        return false;
      case 'checkbox-group':
        return [];
      case 'dropdown':
        return (el.props as DvDropdownParams).isMultiSelect ? [] : null;
      case 'radio': {
        const items = (el.props as DvRadioGroupParams).items as
          | { id?: unknown }[]
          | undefined;
        const isBooleanRadio = items?.every(i => typeof i.id === 'boolean');
        return isBooleanRadio ? false : null;
      }
      case 'number':
      case 'duration':
        return null;
      default:
        return null;
    }
  }
}
